import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";

import { authClient } from "lib/auth/authClient";

/** Generous, but bounded: this name lands on a page we're asking people to trust. */
const MAX_CLIENT_NAME_LENGTH = 80;

export type OAuthClientDetails = {
  /** The client's own display name. Attacker-chosen text — see below. */
  name?: string;
  /** Fallback for when there's no name: the host, or the raw id. Never empty. */
  identity: string;
  /** Host of the `client_id` URL. Self-registering clients only — ours have opaque ids. */
  host?: string;
};

/**
 * Looks up the client's display name, so the consent screen can name it rather than quote an opaque
 * id.
 *
 * **The name is hostile text** — a self-registering client serves it from a URL of its own choosing,
 * onto a page whose job is to get someone to click Allow. Hence the length cap, React escaping, and
 * the host shown alongside. `logo_uri` isn't rendered: that would fetch an attacker-chosen URL.
 */
export function useOAuthClientDetails(clientId: string | undefined, oauthQuery: string) {
  const isSigned = new URLSearchParams(oauthQuery).has("sig");

  return useQuery({
    // Keyed on the query too: without it, a visit with no signature would cache its failed lookup
    // under the plain client id and a later real authorization would reuse it.
    queryKey: ["oauth-client", clientId, oauthQuery],
    // No signature means the endpoint would 400, so don't ask.
    enabled: Boolean(clientId) && isSigned,
    // The answer can't change mid-authorization, and a failure shouldn't block the page.
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<OAuthClientDetails> => {
      // Never rejects: a name we couldn't look up must not stop someone approving or refusing.
      const { identity, host } = clientIdentity(clientId);
      try {
        const result = await authClient.$fetch<{ client_name?: string }>(
          "/oauth2/public-client-prelogin",
          { method: "POST", body: { client_id: clientId, oauth_query: oauthQuery } },
        );

        if (result?.error) {
          Sentry.captureException(
            new Error(`OAuth client name lookup failed: ${result.error.status ?? "no status"}`),
            { extra: { status: result.error.status, message: result.error.message, clientId } },
          );
          return { identity, host };
        }
        return {
          name: result?.data?.client_name?.slice(0, MAX_CLIENT_NAME_LENGTH),
          identity,
          host,
        };
      } catch (err) {
        Sentry.captureException(err);
        return { identity, host };
      }
    },
  });
}

/**
 * Splits a host so the page can emphasise its end, the way a browser's URL bar does —
 * `stotles.com.attacker.com` borrows our name at the front and belongs to `attacker.com`.
 *
 * Which labels identify the owner is a lookup, not a rule (`stotles.co.uk` owns three), and the only
 * public-suffix list available is server-side and 46KB. Hence a guess at the last two, and hence
 * emphasising rather than replacing: `foo.co.uk` bolds a label short, which hides nothing.
 */
export function splitHost(host: string): { lead: string; owner: string } {
  const labels = host.split(".");
  if (labels.length <= 2) return { lead: "", owner: host };
  const owner = labels.slice(-2).join(".");
  // `lead` keeps its trailing dot, so `lead + owner` is always the original host.
  return { lead: host.slice(0, host.length - owner.length), owner };
}

/**
 * Who is really asking: a self-registering client's id is its metadata URL, and the host serving it is
 * the part it can't fake.
 *
 * `https://` is dropped as noise — CIMD only registers HTTPS ids, so a non-HTTPS one is an edited URL
 * and falls through. A client we provisioned has an opaque id, where "registered by <uuid>" would be
 * worse than nothing.
 */
function clientIdentity(clientId: string | undefined): { identity: string; host?: string } {
  if (!clientId) return { identity: "An application" };
  try {
    const url = new URL(clientId);
    if (url.protocol !== "https:") throw new Error("Client ID is not an HTTPS URL");
    return { identity: url.host, host: url.host };
  } catch {
    // Not an HTTPS URL, so one we provisioned. The id is all there is.
    return { identity: clientId };
  }
}
